import React from "react";
import {
  Box,
  MenuItem,
  Select,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AddButton } from "../../components/SectionComponents";
import { TableCell } from "../../components/TableCell";
import { DUPLICATE_CHILD_MESSAGE } from "../../utils/bomSlots";

const PlaceholderRow = styled(TableRow)(() => ({
  backgroundColor: "#fafafa",
  "& > td": {
    borderTop: "1px dashed #d0d0d0",
    borderBottom: "1px dashed #d0d0d0",
  },
}));

const PlaceholderText = styled(Typography)(() => ({
  fontSize: "14px",
  fontStyle: "italic",
  color: "#757575",
}));

type SlotTemplateOption = { uuid: string; name: string };

type SlotPlaceholderRowProps = {
  slot: BomSlot;
  // Templates the slot can be filled with (already limited to the slot type).
  templates: SlotTemplateOption[];
  // Templates whose base item is already in the BOM (shown, but disabled).
  disabledTemplateUuids?: Set<string>;
  isLoadingTemplates: boolean;
  isPicking: boolean;
  onStartFill: (slot: BomSlot) => void;
  onCancelFill: () => void;
  onSelectTemplate: (templateUuid: string, slot: BomSlot) => void;
  readOnly?: boolean;
};

export const SlotPlaceholderRow: React.FC<SlotPlaceholderRowProps> = ({
  slot,
  templates,
  disabledTemplateUuids,
  isLoadingTemplates,
  isPicking,
  onStartFill,
  onCancelFill,
  onSelectTemplate,
  readOnly = false,
}) => {
  const label = slot.hint ? `Empty: ${slot.hint}` : "Empty slot";
  const isTaken = (uuid: string) => !!disabledTemplateUuids?.has(uuid);
  const noTemplates = !isLoadingTemplates && templates.length === 0;
  const allTaken =
    !isLoadingTemplates &&
    templates.length > 0 &&
    templates.every((template) => isTaken(template.uuid));
  const fillDisabled = readOnly || noTemplates || allTaken;
  const fillTooltip = readOnly
    ? ""
    : noTemplates
    ? `No ${slot.child_type_name} templates can be added here`
    : allTaken
    ? `Every ${slot.child_type_name} template is already in this BOM; adjust its quantity instead`
    : `Add a ${slot.child_type_name} on line ${slot.line_number}`;

  return (
    <PlaceholderRow data-testid={`bom-slot-${slot.line_number}`}>
      <TableCell sx={{ width: "70px", color: "#9e9e9e" }}>
        {slot.line_number}
      </TableCell>
      {isPicking ? (
        <TableCell colSpan={5}>
          <Box sx={{ paddingLeft: 1, paddingRight: 1 }}>
            <Select
              size="small"
              value=""
              onChange={(e) => onSelectTemplate(e.target.value as string, slot)}
              onBlur={onCancelFill}
              displayEmpty
              fullWidth
              autoFocus
              defaultOpen
              disabled={readOnly}
              sx={{ fontSize: "14px", backgroundColor: "#fff" }}
            >
              <MenuItem value="" disabled>
                <em>
                  Select {slot.child_type_name} template for line{" "}
                  {slot.line_number}
                  {slot.hint ? ` (${slot.hint})` : ""}...
                </em>
              </MenuItem>
              {isLoadingTemplates ? (
                <MenuItem disabled>
                  <em>Loading templates...</em>
                </MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem
                    key={template.uuid}
                    value={template.uuid}
                    disabled={isTaken(template.uuid)}
                  >
                    {template.name}
                    {isTaken(template.uuid) && (
                      <em style={{ marginLeft: 8 }}>
                        ({DUPLICATE_CHILD_MESSAGE})
                      </em>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
          </Box>
        </TableCell>
      ) : (
        <>
          <TableCell colSpan={2}>
            <PlaceholderText>{label}</PlaceholderText>
          </TableCell>
          <TableCell>
            <PlaceholderText>{slot.child_type_name}</PlaceholderText>
          </TableCell>
          <TableCell />
          <TableCell sx={{ textAlign: "right" }}>
            <Tooltip title={fillTooltip} placement="left" arrow>
              <span>
                <AddButton
                  size="small"
                  disabled={fillDisabled}
                  onClick={() => onStartFill(slot)}
                  aria-label={`Fill BOM line ${slot.line_number}`}
                  sx={{ fontSize: "18px", padding: "4px" }}
                >
                  +
                </AddButton>
              </span>
            </Tooltip>
          </TableCell>
        </>
      )}
    </PlaceholderRow>
  );
};

export default SlotPlaceholderRow;
